import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api, { formatCurrency, API, downloadAuthedFile } from "../lib/api";
import {
  Download, FileText, CheckCircle, Clock, TrendingUp, Receipt, Crown, Trash2,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import ResponsiveTable from "../components/ResponsiveTable";

const PERIODS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom" },
];

export default function Commissions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [period, setPeriod] = useState("monthly");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [rows, setRows] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [payTarget, setPayTarget] = useState(null);

  const params = () => {
    const p = new URLSearchParams();
    p.set("period", period);
    if (period === "custom") {
      if (start) p.set("start", new Date(start).toISOString());
      if (end) p.set("end", new Date(end + "T23:59:59").toISOString());
    }
    if (sellerFilter) p.set("seller_id", sellerFilter);
    return p;
  };

  const load = async () => {
    const p = params();
    const [d, r, su] = await Promise.all([
      api.get(`/commissions/dashboard?${p}`),
      api.get(`/commissions/report?${p}${statusFilter ? "&status=" + statusFilter : ""}`),
      isAdmin ? api.get("/sales-users") : Promise.resolve({ data: [] }),
    ]);
    setDashboard(d.data);
    setRows(r.data.rows);
    setSalesUsers(su.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period, start, end, sellerFilter, statusFilter]);

  const fileDownload = async (path, name) => {
    try {
      await downloadAuthedFile(path, name);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  const exportCSV = () => fileDownload(`/commissions/export.csv?${params()}`, "gemvault-commissions.csv");

  const downloadStatement = async () => {
    if (!sellerFilter) {
      toast.error("Pick a sales user first to download their statement");
      return;
    }
    fileDownload(
      `/commissions/statement/${sellerFilter}.pdf?${params()}`,
      "gemvault-commission-statement.pdf",
    );
  };

  return (
    <Layout title="Commissions" subtitle="Sales performance & payouts">
      <Toaster theme="dark" position="top-right" />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        <div className="flex items-center gap-1 border border-white/10">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              data-testid={`period-${p.value}`}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] transition-colors ${
                period === p.value ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "text-[#8A8A8A] hover:text-[#EAEAEA]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="luxe-input px-3 py-2 text-sm" data-testid="period-start" />
            <span className="text-[#666]">→</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="luxe-input px-3 py-2 text-sm" data-testid="period-end" />
          </>
        )}
        {isAdmin && (
          <select value={sellerFilter} onChange={(e) => setSellerFilter(e.target.value)} className="luxe-input px-4 py-2 text-sm" data-testid="filter-seller">
            <option value="">All Sales Users</option>
            {salesUsers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="luxe-input px-4 py-2 text-sm" data-testid="filter-status">
          <option value="">All Status</option>
          <option value="Pending">Pending</option>
          <option value="Paid">Paid</option>
        </select>
        <div className="flex-1" />
        <button
          onClick={exportCSV}
          data-testid="export-csv"
          className="btn-outline-gold min-h-[44px] px-4 py-2 inline-flex items-center gap-2 text-xs"
        >
          <Download className="w-3 h-3" /> CSV
        </button>
        <button
          onClick={downloadStatement}
          data-testid="download-statement"
          className="btn-outline-gold min-h-[44px] px-4 py-2 inline-flex items-center gap-2 text-xs"
        >
          <FileText className="w-3 h-3" />
          <span className="hidden sm:inline">Monthly Statement (PDF)</span>
          <span className="sm:hidden">Statement PDF</span>
        </button>
      </div>

      {/* KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
          <KPI overline="Total Sales" value={formatCurrency(dashboard.totals.total_sales)} icon={Receipt} />
          <KPI overline="Total Commission" value={formatCurrency(dashboard.totals.total_commission)} icon={Crown} accent />
          <KPI overline="Paid" value={formatCurrency(dashboard.totals.paid_commission)} icon={CheckCircle} color="#7BA585" />
          <KPI overline="Outstanding" value={formatCurrency(dashboard.totals.pending_commission)} icon={Clock} color="#C99356" />
          <KPI overline="Transactions" value={dashboard.totals.transactions} icon={TrendingUp} />
        </div>
      )}

      {/* Per-seller summary */}
      {dashboard && dashboard.rows.length > 0 && (
        <div className="glass-card p-5 sm:p-7 lg:p-10 mb-10">
          <div className="overline">{dashboard.period}</div>
          <h2 className="serif text-2xl mt-1 mb-6">Performance by Sales User</h2>
          <div className="overflow-x-auto -mx-2 px-2">
            <table className="w-full text-sm min-w-[600px]">
              <thead>
                <tr className="text-left overline text-[0.65rem] border-b border-white/5">
                  <th className="py-3 font-normal">Sales User</th>
                  <th className="py-3 font-normal text-right">Sales</th>
                  <th className="py-3 font-normal text-right">Total Commission</th>
                  <th className="py-3 font-normal text-right">Paid</th>
                  <th className="py-3 font-normal text-right">Outstanding</th>
                  <th className="py-3 font-normal text-right">Txn</th>
                </tr>
              </thead>
              <tbody data-testid="per-seller">
                {dashboard.rows.map((r) => (
                  <tr key={r.seller_id} className="border-b border-white/5 last:border-0">
                    <td className="py-3">{r.seller_name}</td>
                    <td className="py-3 mono text-right">{formatCurrency(r.total_sales)}</td>
                    <td className="py-3 mono text-right text-[#D4AF37]">{formatCurrency(r.total_commission)}</td>
                    <td className="py-3 mono text-right text-[#7BA585]">{formatCurrency(r.paid_commission)}</td>
                    <td className="py-3 mono text-right text-[#C99356]">{formatCurrency(r.pending_commission)}</td>
                    <td className="py-3 mono text-right">{r.transactions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Report list */}
      <CommissionsTable rows={rows} isAdmin={isAdmin} onPay={setPayTarget} onDelete={async (r) => {
        if (!window.confirm(`Delete commission for ${r.seller_name} (${r.sale_invoice_number})? The underlying sale stays intact.`)) return;
        try {
          await api.delete(`/commissions/${r.id}`);
          toast.success("Commission removed");
          load();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Delete failed");
        }
      }} />

      {payTarget && (
        <PayModal
          commission={payTarget}
          onClose={() => setPayTarget(null)}
          onSaved={() => { setPayTarget(null); load(); }}
        />
      )}
    </Layout>
  );
}

function CommissionsTable({ rows, isAdmin, onPay, onDelete }) {
  const columns = [
    { key: "date", label: "Date", width: "110px", cell: (r) => <span className="mono text-xs text-[#8A8A8A]">{(r.sale_created_at || r.created_at)?.slice(0, 10)}</span>, hideOnMobile: true },
    { key: "invoice", label: "Invoice", width: "120px", cell: (r) => <span className="mono text-xs text-[#D4AF37]">{r.sale_invoice_number}</span> },
    { key: "seller", label: "Sales User", cell: (r) => (
      <span className="truncate">
        {r.seller_name}
        {r.split_percent < 100 && <span className="text-[0.65rem] text-[#666] ml-2 mono">({r.split_percent}% split)</span>}
      </span>
    ) },
    { key: "product", label: "Product", cell: (r) => <span className="text-[#8A8A8A] truncate">{r.product_name}</span>, hideOnMobile: true },
    { key: "sale", label: "Sale", align: "right", width: "130px", cell: (r) => <span className="mono">{formatCurrency(r.attributed_sale_amount)}</span> },
    { key: "rate", label: "Rate", align: "right", width: "80px", cell: (r) => <span className="mono text-xs text-[#8A8A8A]">{r.rate_percent?.toFixed(1)}%</span>, hideOnMobile: true },
    { key: "commission", label: "Commission", align: "right", width: "140px", cell: (r) => <span className="mono text-[#D4AF37]">{formatCurrency(r.commission_amount)}</span> },
    { key: "status", label: "Status", width: "110px", cell: (r) => (
      <span className={`status-badge ${r.status === "Paid" ? "status-sold" : "status-reserved"}`}>{r.status}</span>
    ) },
  ];
  const rowActions = (r) => (
    <>
      {isAdmin && r.status === "Pending" && (
        <button
          onClick={() => onPay(r)}
          data-testid={`pay-${r.id}`}
          className="min-h-[44px] sm:min-h-0 text-[#D4AF37] hover:text-[#F3E5AB] text-xs uppercase tracking-wider px-2 py-2"
        >
          Mark Paid
        </button>
      )}
      {r.status === "Paid" && (
        <span className="text-[0.65rem] text-[#666] mono px-2">{(r.payment_date || "").slice(0, 10)}</span>
      )}
      {isAdmin && (
        <button
          onClick={() => onDelete(r)}
          data-testid={`delete-commission-${r.id}`}
          className="min-h-[44px] sm:min-h-0 p-2 text-[#8A8A8A] hover:text-red-400 inline-flex items-center justify-center"
          aria-label="Delete commission"
          title="Delete commission (sale stays intact)"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.25} />
        </button>
      )}
    </>
  );
  return (
    <ResponsiveTable
      columns={columns}
      rows={rows}
      rowKey={(r) => r.id}
      testid="commissions-table"
      rowActions={rowActions}
      emptyMessage="No commissions in this period."
      mobileCard={(r) => ({
        title: <span className="mono text-xs text-[#D4AF37]">{r.sale_invoice_number}</span>,
        subtitle: <span>{r.seller_name} · {(r.sale_created_at || r.created_at)?.slice(0, 10)}</span>,
        fields: [
          { label: "Commission", value: <span className="mono text-[#D4AF37]">{formatCurrency(r.commission_amount)}</span> },
          { label: "Sale", value: <span className="mono">{formatCurrency(r.attributed_sale_amount)}</span> },
          { label: "Rate", value: <span className="mono text-[#8A8A8A]">{r.rate_percent?.toFixed(1)}%</span> },
          { label: "Status", value: <span className={`status-badge ${r.status === "Paid" ? "status-sold" : "status-reserved"}`}>{r.status}</span> },
          { label: "Product", value: <span className="text-[#8A8A8A]">{r.product_name || "—"}</span> },
        ],
        actions: rowActions(r),
      })}
    />
  );
}

function KPI({ overline, value, icon: Icon, accent, color }) {
  return (
    <div className={`glass-card p-5 ${accent ? "relative overflow-hidden" : ""}`}>
      {accent && (
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: "radial-gradient(circle at top right, rgba(212,175,55,0.18), transparent 60%)",
        }} />
      )}
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="overline">{overline}</div>
          <Icon className="w-3.5 h-3.5 text-[#666]" strokeWidth={1.25} />
        </div>
        <div className="serif text-2xl mt-2" style={color ? { color } : {}}>{value}</div>
      </div>
    </div>
  );
}

function PayModal({ commission, onClose, onSaved }) {
  const [payment_date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/commissions/${commission.id}/pay`, {
        payment_date: new Date(payment_date).toISOString(),
        note,
      });
      toast.success("Commission marked Paid");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
      <div className="relative bg-[#0F0F0F] border border-white/10 w-full max-w-md fade-up">
        <div className="border-b border-white/5 px-7 py-5 flex justify-between items-center">
          <div>
            <div className="overline">Mark Commission Paid</div>
            <h3 className="serif text-xl mt-1">{commission.seller_name}</h3>
            <div className="mono text-[0.65rem] text-[#666] mt-0.5">{commission.sale_invoice_number} · {formatCurrency(commission.commission_amount)}</div>
          </div>
          <button onClick={onClose} className="text-[#8A8A8A]">×</button>
        </div>
        <form onSubmit={submit} className="px-7 py-6 space-y-4" data-testid="pay-form">
          <div>
            <div className="overline mb-2">Payment Date</div>
            <input required type="date" value={payment_date} onChange={(e) => setDate(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" data-testid="pay-date" />
          </div>
          <div>
            <div className="overline mb-2">Note</div>
            <input value={note} onChange={(e) => setNote(e.target.value)} className="luxe-input w-full px-4 py-2.5 text-sm" placeholder="Transfer ref / cheque #" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline-gold px-5 py-2.5">Cancel</button>
            <button type="submit" data-testid="submit-pay" className="btn-gold px-5 py-2.5">Confirm Paid</button>
          </div>
        </form>
      </div>
    </div>
  );
}
